import type { ThemeColorWithShade } from '../theme/theme.model';

/**
 * Variant options for label styling.
 * - 'primary': white text on feature color 500 background
 * - 'secondary': feature color 600 text on feature color 100 background (default)
 * - 'tertiary': feature color 700 text, no background
 */
export const LABEL_VARIANTS = ['primary', 'secondary', 'tertiary'] as const;
export type LabelVariant = (typeof LABEL_VARIANTS)[number];

/**
 * Text alignment options for multi-line labels.
 */
export const LABEL_TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type LabelTextAlign = (typeof LABEL_TEXT_ALIGNS)[number];

/**
 * Result of label style computation.
 */
export interface LabelStyleResult {
  backgroundColor: string;
  textColor: string;
}

// -----------------------------------------------------------------------------
// LABEL POSITIONS
// -----------------------------------------------------------------------------

/**
 * Standard element-relative positions (cardinal + diagonal + center).
 * Applicable to all element types that support position.
 */
export type DirectionalLabelPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'center';

/** Canvas-relative positions (anchored to the viewport, not an element). */
export type CanvasLabelPosition =
  | 'canvasTop'
  | 'canvasBottom'
  | 'canvasLeft'
  | 'canvasRight'
  | 'canvasTopLeft'
  | 'canvasTopRight'
  | 'canvasBottomLeft'
  | 'canvasBottomRight'
  | 'canvasCenter';

/** Aligned positions — text rotates along the element's slope direction. Only for slope-driven elements (lines, curves). */
export type AlignedLabelPosition = 'alignedAbove' | 'alignedBelow' | 'alignedCenter';

/** For point labels: directional + canvas (no aligned positions since points have no slope). */
export type PointLabelPosition = DirectionalLabelPosition | CanvasLabelPosition;

/**
 * Label position relative to the element.
 *
 * For **points**: Positions are relative to the point location.
 *
 * For **lines/segments**: The label is placed at the midpoint.
 * - `left`/`right`: Perpendicular to the line direction, relative to the
 *   outer set points (start → end). `left` is on the left side when looking
 *   from start to end, `right` is on the right side.
 * - `top`/`bottom`: Above or below the midpoint (in screen coordinates).
 *
 * Use the `offset` property in label config for fine-tuning placement.
 */
export const LABEL_POSITIONS = [
  'top',
  'bottom',
  'left',
  'right',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'center',
  'canvasTop',
  'canvasBottom',
  'canvasLeft',
  'canvasRight',
  'canvasTopLeft',
  'canvasTopRight',
  'canvasBottomLeft',
  'canvasBottomRight',
  'canvasCenter',
  'alignedAbove',
  'alignedBelow',
  'alignedCenter',
] as const;
/** Full union of all label positions. Used internally by the label system and for slope-driven elements. */
export type LabelPosition = (typeof LABEL_POSITIONS)[number];

/**
 * Label spacing mode.
 * - `'normal'`: default offsets (good for point and function labels)
 * - `'compact'`: tight offsets (good for polygon edge labels in geometry)
 */
export type LabelSpacing = 'compact' | 'normal';

// -----------------------------------------------------------------------------
// CONTENT VARIANTS
// -----------------------------------------------------------------------------

/**
 * Content variant for label text generation.
 * Provides convenient shortcuts for common label formats.
 */
export const LABEL_CONTENT_VARIANTS = [
  'name',
  'coord',
  'name+coord',
  'deg',
  'name+deg',
  'length',
  'name+length',
  'expression',
] as const;
export type LabelContentVariant = (typeof LABEL_CONTENT_VARIANTS)[number];

/** Content variants applicable to point labels. */
export const POINT_CONTENT_VARIANTS = ['name', 'coord', 'name+coord'] as const;
export type PointContentVariant = (typeof POINT_CONTENT_VARIANTS)[number];
/** Content variants applicable to line/segment labels. */
export const LINE_CONTENT_VARIANTS = ['name', 'length', 'name+length'] as const;
export type LineContentVariant = (typeof LINE_CONTENT_VARIANTS)[number];
/** Content variants applicable to angle labels. */
export const ANGLE_CONTENT_VARIANTS = ['name', 'deg', 'name+deg'] as const;
export type AngleContentVariant = (typeof ANGLE_CONTENT_VARIANTS)[number];
/** Content variants applicable to polygon center labels. */
export const POLYGON_CONTENT_VARIANTS = ['name'] as const;
export type PolygonContentVariant = (typeof POLYGON_CONTENT_VARIANTS)[number];
/** Content variants applicable to curve labels. */
export const CURVE_CONTENT_VARIANTS = ['name', 'expression'] as const;
export type CurveContentVariant = (typeof CURVE_CONTENT_VARIANTS)[number];
/** Content variants applicable to circle labels. */
export const CIRCLE_CONTENT_VARIANTS = ['name'] as const;
export type CircleContentVariant = (typeof CIRCLE_CONTENT_VARIANTS)[number];

// Per-element position array (only points have a restricted position set)
export const POINT_LABEL_POSITIONS: readonly PointLabelPosition[] = [
  'top',
  'bottom',
  'left',
  'right',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'center',
  'canvasTop',
  'canvasBottom',
  'canvasLeft',
  'canvasRight',
  'canvasTopLeft',
  'canvasTopRight',
  'canvasBottomLeft',
  'canvasBottomRight',
  'canvasCenter',
];

// -----------------------------------------------------------------------------
// LABEL FIELD CONFIG (which fields/options are valid per element type)
// -----------------------------------------------------------------------------

/** Matches StyleKeyType from the editor — which element category a label belongs to. */
export type LabelStyleKey = 'point' | 'line' | 'polygon' | 'angle' | 'circle' | 'curve';

/** Describes which label fields/options are valid for a given element type. */
export interface LabelFieldConfig {
  readonly contentVariants: readonly string[];
  /** Valid positions, or null if the element type has no position field. */
  readonly positions: readonly string[] | null;
  readonly hasAnchorAt: boolean;
  readonly hasPlacement: boolean;
}

export const LABEL_FIELD_CONFIG: Record<LabelStyleKey, LabelFieldConfig> = {
  point: {
    contentVariants: POINT_CONTENT_VARIANTS,
    positions: POINT_LABEL_POSITIONS,
    hasAnchorAt: false,
    hasPlacement: false,
  },
  line: {
    contentVariants: LINE_CONTENT_VARIANTS,
    positions: [...LABEL_POSITIONS],
    hasAnchorAt: true,
    hasPlacement: false,
  },
  angle: { contentVariants: ANGLE_CONTENT_VARIANTS, positions: null, hasAnchorAt: false, hasPlacement: true },
  polygon: { contentVariants: POLYGON_CONTENT_VARIANTS, positions: null, hasAnchorAt: false, hasPlacement: false },
  curve: {
    contentVariants: CURVE_CONTENT_VARIANTS,
    positions: [...LABEL_POSITIONS],
    hasAnchorAt: true,
    hasPlacement: false,
  },
  circle: {
    contentVariants: CIRCLE_CONTENT_VARIANTS,
    positions: [...LABEL_POSITIONS],
    hasAnchorAt: true,
    hasPlacement: false,
  },
};

// -----------------------------------------------------------------------------
// LABEL CONTEXT (runtime data for placeholders)
// -----------------------------------------------------------------------------

/**
 * Context for point labels.
 */
export interface PointLabelContext {
  type: 'point';
  name: string;
  x: () => number;
  y: () => number;
  expression?: () => string;
}

/**
 * Context for line labels.
 */
export interface LineLabelContext {
  type: 'line';
  name: string;
  length: () => number;
  expression?: () => string;
}

/**
 * Context for polygon labels.
 */
export interface PolygonLabelContext {
  type: 'polygon';
  name: string;
  area: () => number;
  perimeter: () => number;
  expression?: () => string;
}

/**
 * Context for curve labels.
 */
export interface CurveLabelContext {
  type: 'curve';
  name: string;
  /** Reactive LaTeX expression string for the function, e.g. "$2(x-3)^2+1$" */
  expression?: () => string;
}

/**
 * Context for angle labels.
 */
export interface AngleLabelContext {
  type: 'angle';
  name: string;
  degrees: () => number;
  radians: () => number;
  expression?: () => string;
}

/**
 * Discriminated union of all label contexts.
 */
export type LabelContext =
  | PointLabelContext
  | LineLabelContext
  | PolygonLabelContext
  | CurveLabelContext
  | AngleLabelContext;

// -----------------------------------------------------------------------------
// BASE LABEL CONFIG (shared by all element types)
// -----------------------------------------------------------------------------

/**
 * Base label configuration shared by all element types.
 * Contains every field that applies universally.
 */
export interface BaseLabelConfig {
  visible?: boolean;
  variant?: LabelVariant;
  /**
   * Theme color for the label.
   *
   * For **primary** and **secondary** variants, only the color name is used
   * (the shade is determined by the variant). For **tertiary**, an explicit
   * shade overrides the default text shade (700).
   *
   * @example 'neutral'      // all variants: uses default shades
   * @example 'neutral-500'  // tertiary: text rendered at shade 500
   */
  color?: ThemeColorWithShade;
  /** Custom format function for complex programmatic logic (overrides contentVariant) */
  formatFn?: (context: LabelContext) => string;
  /**
   * Label text with optional template placeholders for dynamic values.
   * Overrides both contentVariant and formatFn.
   *
   * If text contains `{placeholder}` syntax, values are interpolated dynamically.
   * Otherwise, text is displayed as-is (static).
   *
   * Available placeholders depend on element type:
   * - Points: `{name}`, `{x}`, `{y}`
   * - Lines: `{name}`, `{length}`
   * - Angles: `{name}`, `{deg}`, `{rad}`
   * - Polygons: `{name}`, `{area}`, `{perimeter}`
   *
   * @example "Hello" → "Hello" (static text)
   * @example "Δx = {length}" → "Δx = 5.00" (template)
   * @example "{name} at ({x}, {y})" → "A at (2.00, 3.00)" (template)
   */
  text?: string;
  /**
   * Additional pixel offset from the computed position [dx, dy].
   * This is added to the position-based offset for fine-tuning label placement.
   * Positive dx moves right, positive dy moves up.
   * @example [10, -5] // Move 10px right and 5px down from computed position
   */
  offset?: [number, number];
  /**
   * Number of decimal places for numeric values in this label.
   * Overrides the default precision for coordinates, lengths, degrees, etc.
   * @example 0 → "2", 1 → "2.0", 3 → "2.000"
   */
  precision?: number;
  /**
   * When true, the label is hidden by default and shown on hover over the element.
   */
  popup?: boolean;
  /**
   * Font size in pixels. Overrides the theme default.
   */
  fontSize?: number;
  /**
   * Text alignment for multi-line labels.
   */
  textAlign?: LabelTextAlign;
  /**
   * Controls base offset distance from the element.
   * - `'normal'`: standard offsets (default, good for point and function labels)
   * - `'compact'`: tight offsets (good for polygon edge labels in geometry)
   * @default 'normal'
   */
  spacing?: LabelSpacing;
}

// -----------------------------------------------------------------------------
// PER-ELEMENT LABEL CONFIGS
// -----------------------------------------------------------------------------

/** Label configuration for point, glider, and handle elements. */
export interface PointLabelConfig extends BaseLabelConfig {
  /** Position relative to the point. All directional and canvas positions. */
  position?: PointLabelPosition;
  contentVariant?: PointContentVariant;
}

/** Label configuration for line/segment elements (also used for polygon border labels). */
export interface LineLabelConfig extends BaseLabelConfig {
  /** Position relative to the line midpoint. Supports all positions including aligned (rotated along line). */
  position?: LabelPosition;
  contentVariant?: LineContentVariant;
  /**
   * Where along the line to place the label anchor.
   * - `'start'` → 0 (beginning)
   * - `'middle'` → 0.5 (default)
   * - `'end'` → 1 (end)
   * - number → clamped to [0, 1]
   * @default 0.5
   */
  anchorAt?: number | 'start' | 'middle' | 'end';
}

/** Label configuration for angle elements. */
export interface AngleLabelConfig extends BaseLabelConfig {
  contentVariant?: AngleContentVariant;
  /**
   * Controls whether the label appears inside or outside the angle arc.
   * - `'inside'`: Label on the interior bisector (same side as the arc).
   * - `'outside'`: Label on the exterior bisector (opposite side of the arc).
   * @default 'inside'
   */
  placement?: 'inside' | 'outside';
}

/** Label configuration for polygon center labels. */
export interface PolygonLabelConfig extends BaseLabelConfig {
  contentVariant?: PolygonContentVariant;
}

/** Label configuration for curve elements (function graphs, parametric, data). */
export interface CurveLabelConfig extends BaseLabelConfig {
  /** Position relative to the anchor point on the curve. Supports all positions including aligned. */
  position?: LabelPosition;
  contentVariant?: CurveContentVariant;
  /**
   * Where along the curve to place the label anchor.
   * - `'start'` → 0 (beginning of domain)
   * - `'middle'` → 0.5 (default)
   * - `'end'` → 1 (end of domain)
   * - number → clamped to [0, 1]
   * @default 0.5
   */
  anchorAt?: number | 'start' | 'middle' | 'end';
}

/** Label configuration for circle elements. */
export interface CircleLabelConfig extends BaseLabelConfig {
  /** Position relative to the anchor point on the circle. */
  position?: LabelPosition;
  contentVariant?: CircleContentVariant;
  /**
   * Where along the circle to place the label anchor.
   * Maps to angular position: 0 = right, 0.25 = top (default for 'middle'), 0.5 = left, etc.
   * @default 'middle'
   */
  anchorAt?: number | 'start' | 'middle' | 'end';
}

// -----------------------------------------------------------------------------
// UNIVERSAL LABEL CONFIG (backward compat + generic contexts)
// -----------------------------------------------------------------------------

/**
 * Universal label configuration containing all possible fields.
 * Used by `resolveLabelConfig()`, editor UI, and generic display config contexts
 * where the element type is not known at compile time.
 *
 * For type-safe per-element configs, use `PointLabelConfig`, `LineLabelConfig`,
 * `AngleLabelConfig`, `PolygonLabelConfig`, `CurveLabelConfig`, or `CircleLabelConfig`.
 */
export interface ElementLabelConfig extends BaseLabelConfig {
  /** Position of the label relative to the element */
  position?: LabelPosition;
  /** Content variant for automatic text generation */
  contentVariant?: LabelContentVariant;
  /**
   * Where along a line/curve to place the label anchor. Ignored for points/angles.
   * - `'start'` → 0 (beginning of line/curve domain)
   * - `'middle'` → 0.5 (default)
   * - `'end'` → 1 (end of line/curve domain)
   * - number → clamped to [0, 1]
   * @default 0.5
   */
  anchorAt?: number | 'start' | 'middle' | 'end';
  /**
   * For angle labels: controls whether the label appears inside or outside the angle arc.
   * Ignored for non-angle elements.
   * @default 'inside'
   */
  placement?: 'inside' | 'outside';
}

// -----------------------------------------------------------------------------
// RESOLVED LABEL CONFIG (internal runtime type)
// -----------------------------------------------------------------------------

/**
 * Resolved label configuration with all required fields.
 */
export interface ResolvedLabelConfig {
  visible: boolean;
  variant: LabelVariant;
  color: ThemeColorWithShade;
  position: LabelPosition;
  /** True when the user explicitly set `position` (not using the default). */
  positionIsExplicit: boolean;
  contentVariant: LabelContentVariant;
  formatFn?: (context: LabelContext) => string;
  text?: string;
  offset: [number, number];
  precision?: number;
  popup?: boolean;
  fontSize?: number;
  textAlign?: LabelTextAlign;
  anchorAt?: number | 'start' | 'middle' | 'end';
  spacing: LabelSpacing;
  /** For angle labels: 'inside' (default) or 'outside' the arc. */
  placement?: 'inside' | 'outside';
}

// -----------------------------------------------------------------------------
// RESOLVE FUNCTION
// -----------------------------------------------------------------------------

/**
 * Normalize a label config input to a resolved config.
 */
export function resolveLabelConfig(
  config: ElementLabelConfig | boolean | undefined,
  defaults: Partial<ResolvedLabelConfig> = {},
): ResolvedLabelConfig {
  if (config === false || config === undefined) {
    return {
      visible: false,
      variant: defaults.variant ?? 'secondary',
      color: defaults.color ?? 'neutral',
      position: defaults.position ?? 'topRight',
      positionIsExplicit: false,
      contentVariant: defaults.contentVariant ?? 'name',
      offset: defaults.offset ?? [0, 0],
      spacing: defaults.spacing ?? 'normal',
      placement: defaults.placement,
    };
  }

  if (config === true) {
    return {
      visible: true,
      variant: defaults.variant ?? 'secondary',
      color: defaults.color ?? 'neutral',
      position: defaults.position ?? 'topRight',
      positionIsExplicit: false,
      contentVariant: defaults.contentVariant ?? 'name',
      offset: defaults.offset ?? [0, 0],
      spacing: defaults.spacing ?? 'normal',
      placement: defaults.placement,
    };
  }

  return {
    visible: config.visible ?? true,
    variant: config.variant ?? defaults.variant ?? 'secondary',
    color: config.color ?? defaults.color ?? 'neutral',
    position: config.position ?? defaults.position ?? 'topRight',
    positionIsExplicit: config.position !== undefined,
    contentVariant: config.contentVariant ?? defaults.contentVariant ?? 'name',
    formatFn: config.formatFn,
    text: config.text,
    offset: config.offset ?? defaults.offset ?? [0, 0],
    precision: config.precision ?? defaults.precision,
    popup: config.popup ?? defaults.popup,
    fontSize: config.fontSize ?? defaults.fontSize,
    textAlign: config.textAlign ?? defaults.textAlign,
    anchorAt: config.anchorAt ?? defaults.anchorAt,
    spacing: config.spacing ?? defaults.spacing ?? 'normal',
    placement: config.placement ?? defaults.placement,
  };
}
